import { useState } from "react";
import { motion } from "framer-motion";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/lib/data";
import { WaitlistForm } from "@/components/WaitlistForm";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Minus, Plus } from "lucide-react";

export const ProductCard = ({ product, index = 0 }) => {
  const { addItem } = useCart();
  const [size, setSize] = useState(product.sizes?.[0] || "");
  const [colour, setColour] = useState(product.colours?.[0] || "");
  const [qty, setQty] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [zoomed, setZoomed] = useState(false);

  const isComingSoon = product.coming_soon;
  const cta = isComingSoon ? "Notify Me" : product.preorder ? "Pre-Order" : "Add to Cart";

  const handleAdd = () => {
    const hasColourOpt = product.variants?.some((v) => v.option2);
    const variant = product.variants?.find(
      (v) => (!size || v.option1 === size || v.title === size) &&
             (!hasColourOpt || !colour || v.option2 === colour)
    ) || product.variants?.[0];
    addItem({
      product_id: product.id,
      title: product.title,
      image: product.images[0],
      price: product.price,
      currency: product.currency,
      size, colour, quantity: qty,
      variant_id: variant?.id || null,
      preorder: product.preorder,
    });
    setDialogOpen(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      className="group"
      data-testid={`product-card-${product.id}`}
    >
      <Dialog open={zoomOpen} onOpenChange={(o) => { setZoomOpen(o); if (!o) setZoomed(false); }}>
        <DialogTrigger asChild>
          <button
            type="button"
            data-testid={`product-image-${product.id}`}
            className="relative block w-full overflow-hidden rounded-3xl bg-white aspect-[4/5] cursor-zoom-in"
          >
            <img
              src={product.images[0]}
              alt={product.title}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            {product.badge && (
              <span className="absolute top-4 left-4 bg-cream/95 text-maroon text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-full">
                {product.badge}
              </span>
            )}
          </button>
        </DialogTrigger>
        <DialogContent className="bg-cream max-w-3xl rounded-3xl p-3" data-testid={`product-zoom-${product.id}`}>
          <DialogHeader>
            <DialogTitle className="sr-only">{product.title}</DialogTitle>
          </DialogHeader>
          <div className="overflow-auto max-h-[82vh] rounded-2xl">
            <img
              src={product.images[0]}
              alt={product.title}
              onClick={() => setZoomed((z) => !z)}
              className={`w-full h-auto object-contain transition-transform duration-300 origin-center ${zoomed ? "scale-[1.8] cursor-zoom-out" : "cursor-zoom-in"}`}
            />
          </div>
          <p className="text-center text-xs text-inkbrown/50 mt-1">Click image to {zoomed ? "zoom out" : "zoom in"}</p>
        </DialogContent>
      </Dialog>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <div className="pt-4 flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-lg text-inkbrown leading-tight">{product.title}</h3>
            <div className="mt-1 flex items-center gap-2">
              {product.price_tbc ? (
                <span className="text-maroon font-medium">Price TBC</span>
              ) : (
                <span className="text-maroon font-medium">{formatPrice(product.price, product.currency)}</span>
              )}
              {product.compare_at && (
                <span className="text-sm text-warmneutral line-through">{formatPrice(product.compare_at, product.currency)}</span>
              )}
            </div>
          </div>
        </div>
        <DialogTrigger asChild>
          <button
            data-testid={`product-cta-${product.id}`}
            className="mt-3 w-full h-11 rounded-full border border-maroon text-maroon hover:bg-maroon hover:text-cream transition-colors text-sm tracking-wide"
          >
            {cta}
          </button>
        </DialogTrigger>

        <DialogContent className="bg-cream max-w-lg rounded-3xl" data-testid={`product-dialog-${product.id}`}>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-maroon text-left">{product.title}</DialogTitle>
          </DialogHeader>
          <div className="flex gap-5">
            <img src={product.images[0]} alt={product.title} className="w-32 h-40 object-cover rounded-2xl" />
            <div className="flex-1">
              <p className="text-maroon font-medium text-lg">{product.price_tbc ? "Price TBC" : formatPrice(product.price, product.currency)}</p>
              <p className="text-sm text-inkbrown/70 mt-2 leading-relaxed">{product.description}</p>
              {product.details?.length > 0 && (
                <ul className="mt-3 space-y-1.5">
                  {product.details.map((d) => (
                    <li key={d} className="flex items-center gap-2 text-sm text-inkbrown/80">
                      <span className="w-1.5 h-1.5 rounded-full bg-rosegold shrink-0" />
                      {d}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {isComingSoon ? (
            <div className="mt-2 space-y-3">
              <p className="text-sm text-inkbrown/70">This piece is coming soon. Join the waitlist to be first to know.</p>
              <WaitlistForm source={`product-${product.id}`} placeholder="Your email" />
            </div>
          ) : (
            <div className="space-y-4 mt-2">
              {product.preorder && (
                <div className="rounded-2xl bg-subtle p-4 text-sm text-inkbrown/80 leading-relaxed">
                  <strong className="text-maroon">Pre-order:</strong> Please note this item is available for pre-order.
                  Orders will be placed with our supplier once the pre-order window closes.
                  {product.ship_estimate ? ` ${product.ship_estimate}.` : " Estimated dispatch times will be clearly communicated."}
                </div>
              )}

              {product.sizes?.length > 1 && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-warmneutral mb-2">Size</p>
                  <div className="flex flex-wrap gap-2">
                    {product.sizes.map((s) => (
                      <button
                        key={s}
                        onClick={() => setSize(s)}
                        data-testid={`size-option-${s}`}
                        className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                          size === s ? "bg-maroon text-cream border-maroon" : "border-secondary text-inkbrown hover:border-maroon"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {product.colours?.length > 1 && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-warmneutral mb-2">Colour</p>
                  <div className="flex flex-wrap gap-2">
                    {product.colours.map((c) => (
                      <button
                        key={c}
                        onClick={() => setColour(c)}
                        data-testid={`colour-option-${c}`}
                        className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                          colour === c ? "bg-maroon text-cream border-maroon" : "border-secondary text-inkbrown hover:border-maroon"
                        }`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-4">
                <p className="text-xs uppercase tracking-widest text-warmneutral">Qty</p>
                <div className="flex items-center gap-4 border border-secondary rounded-full px-4 py-2">
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="text-inkbrown/60 hover:text-maroon"><Minus className="w-4 h-4" /></button>
                  <span className="w-5 text-center">{qty}</span>
                  <button onClick={() => setQty((q) => q + 1)} className="text-inkbrown/60 hover:text-maroon"><Plus className="w-4 h-4" /></button>
                </div>
              </div>

              <button
                onClick={handleAdd}
                data-testid={`add-to-cart-${product.id}`}
                className="w-full h-12 rounded-full bg-maroon text-cream hover:bg-maroon-dark transition-transform hover:-translate-y-0.5"
              >
                {product.preorder ? "Pre-order now" : "Add to cart"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};
